import { EditorNode } from '../core/EditorNode';

// Constants
const MAX_PREVIEW_SIZE = 140;
const MIN_PREVIEW_WIDTH = 48;
const MIN_PREVIEW_HEIGHT = 48;
const MAX_RESIZE_SIZE = 400;

const MATERIAL_ICON = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>`;
const REFERENCE_ICON = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path><path d="M2 12l10 5 10-5"></path></svg>`;
const LOADER2_ICON = `<svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>`;

let activeSelectedImageNode = null;
let isOutsideDeselectBound = false;

const deselectAllImageNodes = (exceptContainer = null) => {
  document.querySelectorAll('[data-type="image"]').forEach((node) => {
    if (exceptContainer && node === exceptContainer) return;

    const setSelected = node._setSelected;
    if (typeof setSelected === 'function') {
      setSelected(false);
      return;
    }

    const resizeHandles = node.querySelector('.resize-handles');
    if (resizeHandles) resizeHandles.style.opacity = '0';
    const wrapper = node.querySelector('.img-wrapper');
    if (wrapper) {
      const inner = wrapper.querySelector('span:first-child');
      if (inner) {
        inner.classList.remove('border-neutral-900');
        inner.classList.add('border-transparent');
      }
    }
  });
};

const ensureOutsideDeselectListener = () => {
  if (isOutsideDeselectBound) return;
  isOutsideDeselectBound = true;

  document.addEventListener(
    'click',
    (e) => {
      if (!activeSelectedImageNode) return;
      if (activeSelectedImageNode.contains(e.target)) return;
      activeSelectedImageNode._setSelected?.(false);
      activeSelectedImageNode = null;
    },
    true
  );
};

export class MaterialNode extends EditorNode {
  constructor() {
    super('image');
  }

  get materialUsageKeys() {
    return [
      { id: 'background', label: 'Background' },
      { id: 'product', label: 'Product' },
      { id: 'model', label: 'Model' },
      { id: 'logo', label: 'Logo' },
      { id: 'qrcode', label: 'QR Code' },
      { id: 'icon', label: 'Icon' },
      { id: 'decoration', label: 'Decoration' },
      { id: 'texture', label: 'Texture' },
      { id: 'chart', label: 'Chart' },
    ];
  }

  match(element) {
    return element.getAttribute('data-type') === 'image';
  }

  render(data, context = {}) {
    const { onRemove, onClick, onSync, makeDraggable } = context;
    const asset = data;

    const container = document.createElement('span');
    container.contentEditable = 'false';
    container.setAttribute('data-type', 'image');
    container.setAttribute('data-asset-id', asset.id);
    container.setAttribute('data-url', asset.url);
    container.setAttribute('data-tag', asset.tag || '');

    // Closed-loop data attributes
    const w = asset.width || asset.meta?.w || 128;
    const h = asset.height || asset.meta?.h || 128;
    container.setAttribute('data-width', w);
    container.setAttribute('data-height', h);
    container.setAttribute('data-filename', asset.name || asset.filename || '');
    // Default to isMaterial=true unless explicitly false
    container.setAttribute('data-is-material', asset.isMaterial !== false ? 'true' : 'false');
    const uploadStatus =
      asset?.uploadStatus || (asset?.loading ? 'uploading' : asset?.uploadStatus) || '';
    const isLoading = asset?.loading === true || uploadStatus === 'uploading';
    const progress = Number.isFinite(asset?.progress)
      ? Math.max(0, Math.min(100, asset.progress))
      : 0;
    container.setAttribute('data-loading', isLoading ? 'true' : 'false');
    container.setAttribute(
      'data-upload-status',
      uploadStatus || (isLoading ? 'uploading' : 'done')
    );
    container.setAttribute('data-upload-progress', String(progress));

    container.className =
      'inline-flex flex-col relative align-middle mx-1 select-none group vertical-align-baseline';
    container.style.verticalAlign = 'baseline';

    // Image wrapper
    const imgWrapper = document.createElement('span');
    imgWrapper.className =
      'img-wrapper relative flex items-center justify-center bg-neutral-100 rounded-xl overflow-visible';

    // Inner wrapper for border and clipping
    const innerWrapper = document.createElement('span');
    innerWrapper.className =
      'absolute inset-0 rounded-xl border border-transparent group-hover:border-neutral-900 transition-colors duration-200 overflow-hidden';

    // Determine preview size
    const aspectRatio = w / h;
    let previewWidth = asset.previewWidth;
    if (!previewWidth) {
      if (aspectRatio >= 1) {
        previewWidth = Math.min(w, MAX_PREVIEW_SIZE);
      } else {
        previewWidth = Math.min(w, MAX_PREVIEW_SIZE * aspectRatio);
      }
    }
    imgWrapper.style.width = `${Math.round(previewWidth)}px`;
    imgWrapper.style.aspectRatio = `${aspectRatio}`;

    // Image element
    const img = document.createElement('img');
    let src = asset.url;
    // Handle special URL formats (e.g. `url`)
    if (typeof src === 'string' && src.startsWith('`') && src.endsWith('`')) {
      src = src.slice(1, -1);
    }
    img.src = src;
    img.alt = asset.name || 'image';
    img.className = 'w-full h-full object-cover pointer-events-none block';
    img.onload = () => {
      onSync?.();
    };
    innerWrapper.appendChild(img);
    imgWrapper.appendChild(innerWrapper);

    const uploadOverlay = document.createElement('div');
    uploadOverlay.className =
      'upload-overlay absolute inset-0 rounded-xl flex flex-col items-center justify-center gap-2 text-white z-20';
    uploadOverlay.style.display = isLoading || uploadStatus === 'failed' ? 'flex' : 'none';
    uploadOverlay.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const overlayCard = document.createElement('div');
    overlayCard.className =
      'flex flex-col items-center justify-center gap-2 px-3 py-2 rounded-lg backdrop-blur-sm';
    overlayCard.style.background =
      uploadStatus === 'failed' ? 'rgba(220, 38, 38, 0.65)' : 'rgba(0, 0, 0, 0.45)';

    const statusRow = document.createElement('div');
    statusRow.className = 'flex items-center justify-center gap-2';

    const spinner = document.createElement('div');
    spinner.className = 'upload-spinner';
    spinner.innerHTML = LOADER2_ICON;
    spinner.style.display = uploadStatus === 'failed' ? 'none' : 'block';

    const statusText = document.createElement('div');
    statusText.className = 'upload-status text-xs font-medium';
    statusText.textContent =
      uploadStatus === 'failed' ? 'Upload failed' : `Uploading ${Math.round(progress)}%`;

    statusRow.appendChild(spinner);
    statusRow.appendChild(statusText);

    const progressTrack = document.createElement('div');
    progressTrack.className = 'upload-progress-track w-32 h-1.5 rounded-full overflow-hidden';
    progressTrack.style.background = 'rgba(255, 255, 255, 0.25)';
    progressTrack.style.display = uploadStatus === 'failed' ? 'none' : 'block';

    const progressBar = document.createElement('div');
    progressBar.className = 'upload-progress-bar h-full rounded-full';
    progressBar.style.background = 'rgba(255, 255, 255, 0.9)';
    progressBar.style.width = `${Math.round(progress)}%`;
    progressTrack.appendChild(progressBar);

    overlayCard.appendChild(statusRow);
    overlayCard.appendChild(progressTrack);
    uploadOverlay.appendChild(overlayCard);
    imgWrapper.appendChild(uploadOverlay);

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.className =
      'absolute top-2 right-2 w-5 h-5 bg-neutral-900 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 cursor-pointer shadow-sm hover:scale-110';
    deleteBtn.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
    deleteBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const assetId = container.getAttribute('data-asset-id');
      container.remove();
      if (activeSelectedImageNode === container) activeSelectedImageNode = null;
      onRemove?.(assetId);
      onSync?.();
    };
    imgWrapper.appendChild(deleteBtn);

    // Usage tag label
    const usageLabel = document.createElement('div');
    usageLabel.className = `usage-trigger absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full transition-opacity duration-200 cursor-pointer z-10 max-w-[85%] ${
      asset.tag
        ? 'bg-black/60 text-white shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100'
        : 'bg-black/60 text-white/90 shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100'
    }`;
    usageLabel.innerHTML = `<span class="truncate">${asset.tag || 'Add usage'}</span><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0"><polyline points="6 9 12 15 18 9"></polyline></svg>`;

    usageLabel.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(asset, container, e);
    };
    imgWrapper.appendChild(usageLabel);

    // Type indicator (Material vs Reference)
    const typeIndicator = document.createElement('div');
    typeIndicator.className =
      'type-indicator absolute top-2 left-2 w-5 h-5 bg-black/40 backdrop-blur-sm text-white rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 cursor-pointer hover:bg-black/60';
    const isMaterial = asset.isMaterial !== false; // Default to true
    typeIndicator.innerHTML = isMaterial ? MATERIAL_ICON : REFERENCE_ICON;
    typeIndicator.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const currentIsMaterial = container.getAttribute('data-is-material') === 'true';
      const newIsMaterial = !currentIsMaterial;

      asset.isMaterial = newIsMaterial;
      container.setAttribute('data-is-material', newIsMaterial ? 'true' : 'false');

      typeIndicator.innerHTML = newIsMaterial ? MATERIAL_ICON : REFERENCE_ICON;
      onSync?.();
    };
    imgWrapper.appendChild(typeIndicator);

    // Resize Handles
    const resizeHandles = this.createResizeHandles(imgWrapper, container, asset, onSync);
    imgWrapper.appendChild(resizeHandles);

    // Selection state handling
    let isSelected = false;
    const setSelected = (selected) => {
      isSelected = selected;
      if (selected) {
        resizeHandles.style.opacity = '1';
        innerWrapper.classList.remove('border-transparent');
        innerWrapper.classList.add('border-neutral-900');
        usageLabel.classList.remove('opacity-0');
        usageLabel.classList.add('opacity-100');
      } else {
        resizeHandles.style.opacity = '0';
        innerWrapper.classList.remove('border-neutral-900');
        innerWrapper.classList.add('border-transparent');
        usageLabel.classList.remove('opacity-100');
        usageLabel.classList.add('opacity-0');
      }
    };
    container._setSelected = setSelected;
    ensureOutsideDeselectListener();

    imgWrapper.onclick = (e) => {
      if (container.getAttribute('data-loading') === 'true') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const nextSelected = !isSelected;
      deselectAllImageNodes(container);
      setSelected(nextSelected);
      activeSelectedImageNode = nextSelected ? container : null;
    };

    container.appendChild(imgWrapper);

    // Make draggable
    if (makeDraggable) {
      makeDraggable(container, asset);
    }

    return container;
  }

  update(element, data, context) {
    const { onSync } = context || {};

    const raw = data && typeof data === 'object' ? data : {};
    const rawAsset = raw.asset && typeof raw.asset === 'object' ? raw.asset : null;
    const merged = rawAsset ? { ...rawAsset, ...raw } : raw;

    const currentId = element.getAttribute('data-asset-id') || '';
    const currentUrl = element.getAttribute('data-url') || '';
    const currentTag = element.getAttribute('data-tag') || '';
    const currentW = element.getAttribute('data-width') || '';
    const currentH = element.getAttribute('data-height') || '';
    const currentFilename = element.getAttribute('data-filename') || '';
    const currentLoading = element.getAttribute('data-loading') === 'true';
    const currentUploadStatus = element.getAttribute('data-upload-status') || '';
    const currentProgress = parseFloat(element.getAttribute('data-upload-progress') || '0') || 0;

    const nextId = merged?.id || currentId;
    const nextUrl = merged?.url || merged?.cdn_url || merged?.cdnUrl || currentUrl;
    const nextTag = merged?.tag ?? currentTag;
    const nextIsMaterial =
      merged?.isMaterial === undefined
        ? element.getAttribute('data-is-material') !== 'false'
        : merged.isMaterial !== false;
    const nextW = String(
      merged?.width ||
        merged?.meta?.w ||
        merged?.meta?.width ||
        merged?.meta?.size?.w ||
        currentW ||
        128
    );
    const nextH = String(
      merged?.height ||
        merged?.meta?.h ||
        merged?.meta?.height ||
        merged?.meta?.size?.h ||
        currentH ||
        128
    );
    const nextFilename = merged?.name || merged?.filename || currentFilename;
    const nextLoading = merged?.loading === undefined ? currentLoading : merged.loading === true;
    const nextUploadStatus =
      merged?.uploadStatus === undefined
        ? currentUploadStatus || (nextLoading ? 'uploading' : 'done')
        : String(merged.uploadStatus || '');
    const nextProgressRaw =
      merged?.progress === undefined ? currentProgress : Number(merged.progress);
    const nextProgress = Number.isFinite(nextProgressRaw)
      ? Math.max(0, Math.min(100, nextProgressRaw))
      : 0;

    const requiresRerender =
      nextId !== currentId ||
      nextUrl !== currentUrl ||
      nextW !== currentW ||
      nextH !== currentH ||
      nextFilename !== currentFilename;

    if (requiresRerender) {
      const parent = element.parentNode;
      if (!parent) return;

      const wasActive = activeSelectedImageNode === element;
      if (wasActive) activeSelectedImageNode = null;

      const newNode = this.render(
        {
          ...merged,
          id: nextId,
          url: nextUrl,
          tag: nextTag,
          isMaterial: nextIsMaterial,
          width: Number(nextW),
          height: Number(nextH),
          name: nextFilename,
          loading: nextLoading,
          progress: nextProgress,
          uploadStatus: nextUploadStatus,
        },
        context
      );

      parent.replaceChild(newNode, element);
      if (wasActive) activeSelectedImageNode = newNode;
      onSync?.();
      return;
    }

    element.setAttribute('data-tag', nextTag || '');
    element.setAttribute('data-is-material', nextIsMaterial ? 'true' : 'false');
    element.setAttribute('data-loading', nextLoading ? 'true' : 'false');
    element.setAttribute(
      'data-upload-status',
      nextUploadStatus || (nextLoading ? 'uploading' : 'done')
    );
    element.setAttribute('data-upload-progress', String(nextProgress));

    this.updateUsageLabel(element, nextTag);

    const typeIndicator = element.querySelector('.type-indicator');
    if (typeIndicator) {
      typeIndicator.innerHTML = nextIsMaterial ? MATERIAL_ICON : REFERENCE_ICON;
    }

    const overlay = element.querySelector('.upload-overlay');
    if (overlay) {
      overlay.style.display = nextLoading || nextUploadStatus === 'failed' ? 'flex' : 'none';
      const card = overlay.querySelector('div');
      if (card) {
        card.style.background =
          nextUploadStatus === 'failed' ? 'rgba(220, 38, 38, 0.65)' : 'rgba(0, 0, 0, 0.45)';
      }
      const spinner = overlay.querySelector('.upload-spinner');
      if (spinner) spinner.style.display = nextUploadStatus === 'failed' ? 'none' : 'block';
      const statusText = overlay.querySelector('.upload-status');
      if (statusText) {
        statusText.textContent =
          nextUploadStatus === 'failed'
            ? 'Upload failed'
            : `Uploading ${Math.round(nextProgress)}%`;
      }
      const track = overlay.querySelector('.upload-progress-track');
      if (track) track.style.display = nextUploadStatus === 'failed' ? 'none' : 'block';
      const bar = overlay.querySelector('.upload-progress-bar');
      if (bar) bar.style.width = `${Math.round(nextProgress)}%`;
    }

    onSync?.();
  }

  serialize(element) {
    const loading = element.getAttribute('data-loading') === 'true';
    const uploadStatus = element.getAttribute('data-upload-status') || '';
    if (loading || uploadStatus === 'uploading' || uploadStatus === 'failed') return '';
    const assetId = element.getAttribute('data-asset-id') || '';
    const url = element.getAttribute('data-url') || '';
    const tag = element.getAttribute('data-tag') || '';
    const rawW = parseFloat(element.getAttribute('data-width'));
    const rawH = parseFloat(element.getAttribute('data-height'));
    const filename = element.getAttribute('data-filename') || '';
    const isMaterial = element.getAttribute('data-is-material') !== 'false';

    // Format URL
    const safeFilename = (filename || '').replace(/[\r\n]+/g, ' ');
    const urlStr = url.trim();
    const normalizedUrl =
      urlStr.length >= 2 && urlStr.startsWith('`') && urlStr.endsWith('`')
        ? urlStr.slice(1, -1).trim()
        : urlStr;
    const formattedUrl = ` \`${normalizedUrl}\` `;

    if (!isMaterial) {
      const w = rawW || 120;
      const h = rawH || 120;
      return `@VeesoMaterial(${JSON.stringify({
        id: assetId,
        url: formattedUrl,
        type: 'reference',
        width: w,
        height: h,
        aspectRatio: Math.round((100 * w) / h) / 100,
        tag: tag || 'Background',
      })})`;
    }

    const w = rawW || 128;
    const h = rawH || 128;
    return `@VeesoMaterial(${JSON.stringify({
      id: assetId,
      filename: safeFilename,
      url: formattedUrl,
      width: w,
      height: h,
      aspectRatio: Math.round((100 * w) / h) / 100,
      tag: tag,
    })})`;
  }

  updateUsageLabel(container, tag) {
    const label = container.querySelector('.usage-trigger');
    if (label) {
      const labelText = label.querySelector('span');
      if (labelText) {
        labelText.textContent = tag || 'Add usage';
        labelText.className = 'truncate';
      }

      const icon = label.querySelector('svg');
      if (icon) {
        icon.classList.add('flex-shrink-0');
      }

      // Preserve selection-driven opacity if present
      const isSelected =
        label.classList.contains('opacity-100') && !label.closest('.group').matches(':hover');

      label.className = `usage-trigger absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full transition-opacity duration-200 cursor-pointer z-10 max-w-[85%] ${
        tag ? 'bg-black/60 text-white' : 'bg-black/60 text-white/90'
      } shadow-sm backdrop-blur-sm ${isSelected ? 'opacity-100' : 'opacity-0'} group-hover:opacity-100`;
    }
  }

  createResizeHandles(imgWrapper, container, asset, onSync) {
    const handlesContainer = document.createElement('div');
    handlesContainer.className = 'resize-handles';
    handlesContainer.style.cssText = `
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
    `;

    const corners = [
      { position: 'nw', cursor: 'nwse-resize', style: 'top: -3px; left: -3px;' },
      { position: 'ne', cursor: 'nesw-resize', style: 'top: -3px; right: -3px;' },
      { position: 'sw', cursor: 'nesw-resize', style: 'bottom: -3px; left: -3px;' },
      { position: 'se', cursor: 'nwse-resize', style: 'bottom: -3px; right: -3px;' },
    ];

    const getAspectRatio = () => {
      const w = parseFloat(container.getAttribute('data-width')) || 128;
      const h = parseFloat(container.getAttribute('data-height')) || 128;
      return w / h;
    };

    corners.forEach(({ position, cursor, style }) => {
      const handle = document.createElement('div');
      handle.className = `resize-handle-${position}`;
      handle.style.cssText = `
            position: absolute;
            width: 12px;
            height: 12px;
            border-radius: 7px;
            background: rgb(255, 255, 255);
            border: 2px solid rgb(23, 23, 23);
            box-shadow: rgba(0, 0, 0, 0.2) 0px 1px 3px;
            pointer-events: auto;
            transition: 0.15s;
            ${style}
            cursor: ${cursor};
            z-index: 20;
            transform: scale(1);
        `;

      handle.onmouseenter = () => {
        handle.style.transform = 'scale(1.2)';
      };
      handle.onmouseleave = () => {
        handle.style.transform = 'scale(1)';
      };

      let isResizing = false;
      let startX = 0,
        startY = 0;
      let startWidth = 0;
      let latestWidth = 0;
      let rafId = null;
      let pendingClientX = 0,
        pendingClientY = 0;
      let prevTransition = '',
        prevWillChange = '';

      handle.onmousedown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        isResizing = true;
        startX = e.clientX;
        startY = e.clientY;
        startWidth = imgWrapper.offsetWidth;
        latestWidth = startWidth;
        pendingClientX = startX;
        pendingClientY = startY;
        prevTransition = imgWrapper.style.transition;
        prevWillChange = imgWrapper.style.willChange;
        imgWrapper.style.transition = 'none';
        imgWrapper.style.willChange = 'width';
        container.setAttribute('draggable', 'false');

        document.body.style.cursor = cursor;
        document.body.style.userSelect = 'none';

        const aspectRatio = getAspectRatio();

        const applyResize = () => {
          rafId = null;
          if (!isResizing) return;

          let deltaX = pendingClientX - startX;
          let deltaY = pendingClientY - startY;

          if (position === 'nw' || position === 'sw') deltaX = -deltaX;
          if (position === 'nw' || position === 'ne') deltaY = -deltaY;

          const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY * aspectRatio;

          const minWidthForHeight = MIN_PREVIEW_HEIGHT * aspectRatio;
          const effectiveMinWidth = Math.max(MIN_PREVIEW_WIDTH, minWidthForHeight);

          const clampedWidth = Math.max(
            effectiveMinWidth,
            Math.min(MAX_RESIZE_SIZE, startWidth + delta)
          );

          const newWidth = Math.round(clampedWidth);
          if (newWidth === latestWidth) return;

          latestWidth = newWidth;
          imgWrapper.style.width = `${newWidth}px`;
        };

        const onMouseMove = (e) => {
          if (!isResizing) return;
          pendingClientX = e.clientX;
          pendingClientY = e.clientY;
          if (rafId) return;
          rafId = requestAnimationFrame(applyResize);
        };

        const onMouseUp = () => {
          isResizing = false;
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          imgWrapper.style.transition = prevTransition;
          imgWrapper.style.willChange = prevWillChange;

          // Update container attributes
          const newHeight = Math.round(latestWidth / aspectRatio);
          container.setAttribute('data-width', latestWidth);
          container.setAttribute('data-height', newHeight);

          if (asset) {
            asset.previewWidth = latestWidth;
            asset.width = latestWidth;
            asset.height = newHeight;
          }

          container.setAttribute('draggable', 'true');
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          onSync?.({ forceHeight: true });
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      handlesContainer.appendChild(handle);
    });

    return handlesContainer;
  }
}
